/**
 * User Management Page
 *
 * Admin interface for managing platform users: listing, filtering,
 * role assignment, status changes, and soft deletion.
 *
 * Story: SCRUM-841 — FIX-H04: Admin user management
 * Pattern: Follows GuideApprovalQueue.jsx layout
 */

import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  RefreshCw,
  Eye,
  Shield,
  Ban,
  CheckCircle,
  Trash2,
  X,
  Lock
} from 'lucide-react';

import DashboardLayout from '../../components/layout/DashboardLayout';
import Pagination from '../../components/admin/Pagination';
import SearchBar from '../../components/admin/SearchBar';
import FilterDropdown from '../../components/admin/FilterDropdown';
import PromptModal from '../../components/shared/PromptModal';
import RoleHierarchyTree from '../../components/admin/RoleHierarchyTree';
import {
  useAdminUsers,
  useAdminUserDetail,
  useRoleHierarchy,
  getImpliedRoles
} from '../../hooks/admin/useAdminUsers';

// SCRUM-1806: full role list mirrors apps/api/src/config/role-hierarchy.json.
const ALL_ROLES = [
  'super_admin',
  'admin',
  'staff',
  'customer_support_admin',
  'customer_support',
  'guide',
  'customer',
  'platform_admin',
];

const ROLE_OPTIONS = [
  { value: '', label: 'All Roles' },
  { value: 'customer', label: 'Customer' },
  { value: 'guide', label: 'Guide' },
  { value: 'staff', label: 'Staff' },
  { value: 'customer_support', label: 'Customer Support' },
  { value: 'customer_support_admin', label: 'Customer Support Admin' },
  { value: 'admin', label: 'Admin' },
  { value: 'super_admin', label: 'Super Admin' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'active', label: 'Active' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'deleted', label: 'Deleted' },
];

const SORT_OPTIONS = [
  { value: 'created_at', label: 'Created Date' },
  { value: 'email', label: 'Email' },
  { value: 'first_name', label: 'First Name' },
  { value: 'last_name', label: 'Last Name' },
  { value: 'last_login_at', label: 'Last Login' },
];

function getStatusBadge(status) {
  const styles = {
    active: 'bg-green-100 text-green-800',
    suspended: 'bg-red-100 text-red-800',
    deleted: 'bg-gray-100 text-gray-500',
  };
  return styles[status] || 'bg-gray-100 text-gray-800';
}

function getRoleBadge(role) {
  const styles = {
    customer: 'bg-blue-100 text-blue-800',
    guide: 'bg-emerald-100 text-emerald-800',
    admin: 'bg-purple-100 text-purple-800',
    super_admin: 'bg-amber-100 text-amber-800',
    staff: 'bg-cyan-100 text-cyan-800',
    customer_support: 'bg-teal-100 text-teal-800',
    customer_support_admin: 'bg-indigo-100 text-indigo-800',
    platform_admin: 'bg-slate-100 text-slate-800',
  };
  return styles[role] || 'bg-gray-100 text-gray-800';
}

/**
 * SCRUM-1806 RoleEditorModal — replaces the SCRUM-841 simple-toggle editor.
 *
 * AC-03/04/05/06 implementation:
 *  - Each role checkbox shows whether the role was originally cascade-granted
 *    (lock icon + tooltip with the parent role) so the admin sees the cascade.
 *  - Toggling a higher role updates a live preview that fills in implied roles
 *    (AC-05). Implied roles can be individually unchecked to override (AC-04).
 *  - On save, a confirmation panel appears with the diff (added/removed roles)
 *    before the PUT is sent (AC-06).
 *  - The role hierarchy tree (AC-07/08) is rendered inline.
 *
 * Pure props: parent owns selection state via `editRoles` + `toggleRole`.
 */
function RoleEditorModal({
  email,
  originalRoles,
  originalCascades,
  editRoles,
  toggleRole,
  hierarchy,
  onSave,
  onClose,
}) {
  const [reason, setReason] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  // SCRUM-1806 AC-05: when a role is checked, compute the cascade preview —
  // every implied role that's also auto-checked because of it.
  const cascadePreview = useMemo(() => {
    const map = {};
    if (!hierarchy) { return map; }
    for (const checked of editRoles) {
      const implied = getImpliedRoles(checked, hierarchy);
      for (const r of implied) {
        if (editRoles.includes(r) && !map[r]) {
          map[r] = checked; // first parent that grants it
        }
      }
    }
    return map;
  }, [editRoles, hierarchy]);

  // Original cascade map (from server) — tells us which roles were cascade-
  // granted before the admin started editing. Drives the lock-icon display.
  const originalCascadeMap = useMemo(() => {
    const map = {};
    for (const entry of originalCascades || []) {
      if (entry.grantedBy && entry.grantedBy !== 'manual') {
        map[entry.name] = entry.grantedBy;
      }
    }
    return map;
  }, [originalCascades]);

  const added = editRoles.filter(r => !(originalRoles || []).includes(r));
  const removed = (originalRoles || []).filter(r => !editRoles.includes(r));
  const hasChanges = added.length > 0 || removed.length > 0;

  if (confirmOpen) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
          <div className="p-6">
            <h2 className="text-xl font-semibold mb-2">Confirm role changes</h2>
            <p className="text-gray-700 mb-4 text-sm">
              This will change roles for <strong>{email || 'user'}</strong>.
            </p>
            <div className="space-y-2 mb-4 text-sm">
              {added.length > 0 && (
                <div>
                  <span className="text-green-700 font-medium">Add ({added.length}):</span>
                  <ul className="ml-4 mt-1 list-disc text-gray-700">
                    {added.map(r => <li key={r}><span className="font-mono">{r}</span></li>)}
                  </ul>
                </div>
              )}
              {removed.length > 0 && (
                <div>
                  <span className="text-red-700 font-medium">Remove ({removed.length}):</span>
                  <ul className="ml-4 mt-1 list-disc text-gray-700">
                    {removed.map(r => <li key={r}><span className="font-mono">{r}</span></li>)}
                  </ul>
                </div>
              )}
            </div>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason for role change..."
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              autoFocus
            />
            <p className="text-xs text-gray-500 mt-2">* Reason is required</p>
          </div>
          <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3 rounded-b-lg">
            <button
              onClick={() => setConfirmOpen(false)}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Back
            </button>
            <button
              onClick={() => onSave(reason)}
              disabled={!reason.trim()}
              className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Confirm & save
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xl font-semibold">Edit user roles</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-gray-700 mb-4 text-sm">
            Update roles for <strong>{email || 'user'}</strong>. Granting a
            higher role automatically grants the roles it implies (locked
            below). You can uncheck any cascade-granted role to override it.
          </p>

          <div className="space-y-2 mb-4">
            {ALL_ROLES.map(role => {
              const checked = editRoles.includes(role);
              const cascadedNow = cascadePreview[role]; // cascaded by current selection
              const cascadedOrig = originalCascadeMap[role]; // cascaded in the server state
              const cascadeSource = cascadedNow || cascadedOrig;
              const isCascadeChecked = checked && !!cascadedNow;
              return (
                <label
                  key={role}
                  className={`flex items-center justify-between p-2 rounded-md border ${
                    checked ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleRole(role)}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                    />
                    <span
                      className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${getRoleBadge(role)}`}
                    >
                      {role}
                    </span>
                    {isCascadeChecked && (
                      <span
                        className="inline-flex items-center gap-1 text-xs text-amber-700"
                        title={`Auto-granted by ${cascadeSource}`}
                      >
                        <Lock className="w-3 h-3" />
                        granted by {cascadeSource}
                      </span>
                    )}
                  </div>
                  {checked && cascadedOrig && !cascadedNow && (
                    <span className="text-xs text-orange-700">
                      override of cascade from {cascadedOrig}
                    </span>
                  )}
                </label>
              );
            })}
          </div>

          {hierarchy && <RoleHierarchyTree hierarchy={hierarchy} />}
        </div>
        <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3 rounded-b-lg">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={() => setConfirmOpen(true)}
            disabled={!hasChanges || editRoles.length === 0}
            className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            Review changes
          </button>
        </div>
      </div>
    </div>
  );
}

export default function UserManagement() {
  const navigate = useNavigate();
  const handleNavigate = (id, path) => { if (path) { navigate(path); } };
  const { user: authUser } = useAuth();

  // Filters and data
  const {
    users,
    pagination,
    stats,
    loading,
    error,
    filters,
    updateFilters,
    refresh
  } = useAdminUsers({
    page: 1,
    limit: 25,
    sortBy: 'created_at',
    sortOrder: 'desc'
  });

  // SCRUM-1806: hierarchy for cascade preview + tree visualization
  const { hierarchy } = useRoleHierarchy();

  // Modal state
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [toast, setToast] = useState(null);

  // Detail hook
  const {
    user: selectedUser,
    loading: detailLoading,
    updateRoles,
    updateStatus,
    deleteUser
  } = useAdminUserDetail(selectedUserId);

  // Role editor state
  const [editRoles, setEditRoles] = useState([]);
  // SCRUM-1806: snapshot of the user's original roles + cascade map at the
  // moment the editor opened — used to render lock icons and compute the diff.
  const [originalRoles, setOriginalRoles] = useState([]);
  const [originalCascades, setOriginalCascades] = useState([]);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  // Actions
  const handleViewDetail = (userId) => {
    setSelectedUserId(userId);
    setShowDetailModal(true);
  };

  const handleOpenRoleEditor = (userId) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      setSelectedUserId(userId);
      setEditRoles([...(user.roles || [])]);
      setOriginalRoles([...(user.roles || [])]);
      setOriginalCascades(user.roleCascades || []);
      setShowRoleModal(true);
    }
  };

  const handleSaveRoles = async (reason) => {
    try {
      await updateRoles(editRoles, reason);
      showToast('Roles updated successfully');
      setShowRoleModal(false);
      refresh();
    } catch (err) {
      showToast(err.message || 'Failed to update roles', 'error');
    }
  };

  const handleSuspendUser = (userId) => {
    setSelectedUserId(userId);
    setShowSuspendModal(true);
  };

  const handleConfirmSuspend = async (reason) => {
    try {
      const user = users.find(u => u.id === selectedUserId);
      const newStatus = user?.status === 'suspended' ? 'active' : 'suspended';
      await updateStatus(newStatus, reason);
      showToast(`User ${newStatus === 'suspended' ? 'suspended' : 'activated'} successfully`);
      setShowSuspendModal(false);
      refresh();
    } catch (err) {
      showToast(err.message || 'Failed to update status', 'error');
    }
  };

  const handleDeleteUser = (userId) => {
    setSelectedUserId(userId);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async (reason) => {
    try {
      await deleteUser(reason);
      showToast('User deleted successfully');
      setShowDeleteModal(false);
      refresh();
    } catch (err) {
      showToast(err.message || 'Failed to delete user', 'error');
    }
  };

  // SCRUM-1806 AC-04/05: toggling a role triggers cascade-fill on add but
  // leaves implied roles in place when the parent is unchecked (override-
  // friendly, mirrors the server's grant-only cascade in SCRUM-1805).
  const toggleRole = (role) => {
    setEditRoles(prev => {
      if (prev.includes(role)) {
        if (prev.length === 1) { return prev; } // Must have at least one role
        return prev.filter(r => r !== role);
      }
      const implied = hierarchy ? getImpliedRoles(role, hierarchy) : [];
      const next = new Set(prev);
      next.add(role);
      for (const r of implied) { next.add(r); }
      return [...next];
    });
  };

  const isSuperAdmin = authUser?.user_type === 'super_admin';
  const selectedUserForAction = users.find(u => u.id === selectedUserId);

  return (
    <DashboardLayout userRole="admin" activeNavId="users" onNavigate={handleNavigate}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
          <button
            onClick={refresh}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Stats cards */}
        {stats.byStatus && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <p className="text-sm text-gray-500">Total Users</p>
              <p className="text-2xl font-bold text-gray-900">
                {(stats.byStatus.active || 0) + (stats.byStatus.suspended || 0) + (stats.byStatus.deleted || 0)}
              </p>
            </div>
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <p className="text-sm text-gray-500">Active</p>
              <p className="text-2xl font-bold text-green-600">{stats.byStatus.active || 0}</p>
            </div>
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <p className="text-sm text-gray-500">Suspended</p>
              <p className="text-2xl font-bold text-red-600">{stats.byStatus.suspended || 0}</p>
            </div>
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <p className="text-sm text-gray-500">Deleted</p>
              <p className="text-2xl font-bold text-gray-400">{stats.byStatus.deleted || 0}</p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <SearchBar
            value={filters.search || ''}
            onChange={(value) => updateFilters({ search: value })}
            placeholder="Search by name or email..."
          />
          <FilterDropdown
            label="Role"
            value={filters.role || ''}
            options={ROLE_OPTIONS}
            onChange={(value) => updateFilters({ role: value })}
          />
          <FilterDropdown
            label="Status"
            value={filters.status || ''}
            options={STATUS_OPTIONS}
            onChange={(value) => updateFilters({ status: value })}
          />
          <FilterDropdown
            label="Sort by"
            value={filters.sortBy || 'created_at'}
            options={SORT_OPTIONS}
            onChange={(value) => updateFilters({ sortBy: value })}
          />
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            {error}
          </div>
        )}

        {/* Table */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role(s)</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Verified</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Login</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading && (
                  <tr>
                    <td colSpan={8} className="px-6 py-8 text-center text-gray-500">
                      Loading users...
                    </td>
                  </tr>
                )}
                {!loading && users.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-6 py-8 text-center text-gray-500">
                      No users found
                    </td>
                  </tr>
                )}
                {!loading && users.map(user => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">{user.email}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {user.first_name} {user.last_name}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {(user.roleCascades && user.roleCascades.length > 0
                          ? user.roleCascades
                          : (user.roles || []).map(name => ({ name, grantedBy: 'manual' }))
                        ).map(({ name, grantedBy }) => (
                          <span
                            key={name}
                            title={grantedBy !== 'manual' ? `Auto-granted by ${grantedBy}` : 'Manually assigned'}
                            className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${getRoleBadge(name)} ${
                              grantedBy !== 'manual' ? 'ring-1 ring-amber-400' : ''
                            }`}
                          >
                            {grantedBy !== 'manual' && <Lock className="w-3 h-3" />}
                            {name}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${getStatusBadge(user.status)}`}>
                        {user.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {user.email_verified ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : (
                        <X className="w-4 h-4 text-gray-400" />
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {user.last_login_at ? new Date(user.last_login_at).toLocaleDateString() : 'Never'}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleViewDetail(user.id)}
                          className="p-1 text-gray-500 hover:text-blue-600"
                          title="View detail"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {isSuperAdmin && (
                          <button
                            onClick={() => handleOpenRoleEditor(user.id)}
                            className="p-1 text-gray-500 hover:text-purple-600"
                            title="Edit roles"
                          >
                            <Shield className="w-4 h-4" />
                          </button>
                        )}
                        {user.status !== 'deleted' && (
                          <button
                            onClick={() => handleSuspendUser(user.id)}
                            className="p-1 text-gray-500 hover:text-orange-600"
                            title={user.status === 'suspended' ? 'Activate' : 'Suspend'}
                          >
                            {user.status === 'suspended' ? (
                              <CheckCircle className="w-4 h-4" />
                            ) : (
                              <Ban className="w-4 h-4" />
                            )}
                          </button>
                        )}
                        {isSuperAdmin && user.status !== 'deleted' && (
                          <button
                            onClick={() => handleDeleteUser(user.id)}
                            className="p-1 text-gray-500 hover:text-red-600"
                            title="Delete user"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.totalPages}
            onPageChange={(page) => updateFilters({ page })}
          />
        )}

        {/* Detail Modal */}
        {showDetailModal && selectedUser && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold">User Detail</h2>
                  <button onClick={() => setShowDetailModal(false)} className="text-gray-400 hover:text-gray-600">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {detailLoading ? (
                  <p className="text-gray-500">Loading...</p>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs text-gray-500 uppercase">Email</label>
                      <p className="text-sm font-medium">{selectedUser.email}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-gray-500 uppercase">First Name</label>
                        <p className="text-sm font-medium">{selectedUser.first_name}</p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 uppercase">Last Name</label>
                        <p className="text-sm font-medium">{selectedUser.last_name}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-gray-500 uppercase">Status</label>
                        <p>
                          <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${getStatusBadge(selectedUser.status)}`}>
                            {selectedUser.status}
                          </span>
                        </p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 uppercase">Email Verified</label>
                        <p className="text-sm">{selectedUser.email_verified ? 'Yes' : 'No'}</p>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 uppercase">Roles</label>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {(selectedUser.roles || []).map(role => (
                          <span key={role} className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${getRoleBadge(role)}`}>
                            {role}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-gray-500 uppercase">MFA Enabled</label>
                        <p className="text-sm">{selectedUser.mfa_enabled ? 'Yes' : 'No'}</p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 uppercase">Failed Logins</label>
                        <p className="text-sm">{selectedUser.failed_login_attempts || 0}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-gray-500 uppercase">Created</label>
                        <p className="text-sm">{selectedUser.created_at ? new Date(selectedUser.created_at).toLocaleString() : '-'}</p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 uppercase">Last Login</label>
                        <p className="text-sm">{selectedUser.last_login_at ? new Date(selectedUser.last_login_at).toLocaleString() : 'Never'}</p>
                      </div>
                    </div>
                    {selectedUser.phone && (
                      <div>
                        <label className="text-xs text-gray-500 uppercase">Phone</label>
                        <p className="text-sm">{selectedUser.phone}</p>
                      </div>
                    )}
                    <div>
                      <label className="text-xs text-gray-500 uppercase">User ID</label>
                      <p className="text-xs text-gray-400 font-mono">{selectedUser.id}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Role Editor Modal — SCRUM-1806 cascade-aware editor */}
        {showRoleModal && (
          <RoleEditorModal
            email={selectedUserForAction?.email}
            originalRoles={originalRoles}
            originalCascades={originalCascades}
            editRoles={editRoles}
            toggleRole={toggleRole}
            hierarchy={hierarchy}
            onSave={handleSaveRoles}
            onClose={() => setShowRoleModal(false)}
          />
        )}

        {/* Suspend/Activate Modal */}
        <PromptModal
          isOpen={showSuspendModal}
          onClose={() => setShowSuspendModal(false)}
          onConfirm={handleConfirmSuspend}
          title={selectedUserForAction?.status === 'suspended' ? 'Activate User' : 'Suspend User'}
          message={`${selectedUserForAction?.status === 'suspended' ? 'Activate' : 'Suspend'} ${selectedUserForAction?.email || 'user'}? Please provide a reason.`}
          placeholder="Reason..."
          confirmText={selectedUserForAction?.status === 'suspended' ? 'Activate' : 'Suspend'}
        />

        {/* Delete Modal */}
        <PromptModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleConfirmDelete}
          title="Delete User"
          message={`This will soft-delete ${selectedUserForAction?.email || 'user'}. This sets status to 'deleted'. Please provide a reason.`}
          placeholder="Reason for deletion..."
          confirmText="Delete"
        />

        {/* Toast */}
        {toast && (
          <div className={`fixed bottom-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${
            toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-green-600 text-white'
          }`}>
            {toast.message}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
